import { type DocumentData, limit, orderBy, query } from 'firebase/firestore'

import { callable } from '@/lib/firebase/callable'
import { type Subscription, subscribeCollection } from '@/lib/firebase/subscribe'
import {
  type SetupScope,
  asOneOf,
  asRecord,
  asString,
  asStrings,
  asStringOrNull,
  setupCollection,
  subscribeOne,
  subscribeLatestRun,
  toSetupRun,
  toStageEntryBase,
} from '@/repositories/setup-docs'
import { JOURNEY_PHASES } from '@/types/brand'
import {
  POST_RUN_ERROR_CODES,
  POST_RUN_STAGES,
  type PostBeat,
  type PostBrief,
  type PostRun,
  type PostRunStageEntry,
} from '@/types/post-run'

function toStageEntry(entry: DocumentData): PostRunStageEntry | undefined {
  const id = entry.id
  if (!(POST_RUN_STAGES as readonly string[]).includes(id)) return undefined
  return { ...toStageEntryBase(entry), id, counts: asRecord(entry.counts) } as PostRunStageEntry
}

function toBeat(value: unknown): PostBeat {
  const data = asRecord(value)
  return {
    type: asString(data.type),
    text: asString(data.text),
    quote: asStringOrNull(data.quote),
    by: asStringOrNull(data.by),
    who: asStringOrNull(data.who),
    url: asStringOrNull(data.url),
    block: asString(data.block),
  }
}

function toBrief(value: unknown): PostBrief | null {
  if (value === null || value === undefined) return null
  const data = asRecord(value)
  return {
    beliefSlug: asString(data.beliefSlug),
    topicId: asString(data.topicId),
    phase: asOneOf(data.phase, JOURNEY_PHASES, 'awareness'),
    claim: asString(data.claim),
    ctaType: asString(data.ctaType),
    format: asString(data.format),
    beats: Array.isArray(data.beats) ? data.beats.map(toBeat) : [],
  }
}

/** The post run as the client reads it; a stage the client does not know is dropped. */
export function toPostRun(id: string, data: DocumentData): PostRun {
  const { errorCode, ...run } = toSetupRun(id, data, POST_RUN_STAGES, toStageEntry)
  return {
    ...run,
    ...(errorCode === undefined
      ? {}
      : { errorCode: asOneOf(errorCode, POST_RUN_ERROR_CODES, 'post_run_failed') }),
    startedByUserId: asStringOrNull(data.startedByUserId),
    postId: asStringOrNull(data.postId),
    brief: toBrief(data.brief),
    score: typeof data.score === 'number' ? data.score : null,
    reasons: asStrings(data.reasons),
  }
}

/** Live view of the brand's most recent post run; `null` before the first. */
export function subscribeLatestPostRun(scope: SetupScope): Subscription<PostRun | null> {
  return subscribeLatestRun(scope, 'postRuns', (snapshot) => {
    const data = snapshot.data()
    return data ? toPostRun(snapshot.id, data) : undefined
  })
}

/** Live view of one post run by id; an absent run reads as absent. */
export function subscribePostRun(scope: SetupScope, runId: string): Subscription<PostRun> {
  return subscribeOne(scope, 'postRuns', runId, toPostRun)
}

/** Live view of the brand's newest `count` post runs, newest first. */
export function subscribeRecentPostRuns(scope: SetupScope, count: number): Subscription<PostRun[]> {
  return subscribeCollection(
    query(setupCollection(scope, 'postRuns'), orderBy('createdAt', 'desc'), limit(count)),
    (snapshot) => snapshot.docs.map((doc) => toPostRun(doc.id, doc.data())),
  )
}

/** What a start pins: a belief or a topic; neither lets the rotation choose. */
export interface WritePostInput {
  readonly beliefSlug?: string
  readonly topicId?: string
}

export interface WritePostResult {
  readonly runId: string
}

const writePostCallable = callable<WritePostInput, WritePostResult>('writePost')

/** Opens the brand's post run; resolves the run id. */
export async function writePost(input: WritePostInput = {}): Promise<WritePostResult> {
  return writePostCallable(input)
}
